import type { CallOptions } from '@connectrpc/connect';
import { ApiError } from '@/api/api-error';

export type FromGrpcResponseFnc<M, RESP> = (resp: RESP, params?: any) => M;
export type OnErrorFnc = (error: ApiError) => void;
export type OnSingleDataFnc<M> = (data: M) => void;
export type OnMultipleDataFnc<M> = (data: M[]) => void;

export type ResolveFnc<M> = (value: M) => void;
export type RejectFnc = (reason?: any) => void;

const MIN_DATA_COUNT = 50;
const MAX_DATA_COUNT = 1000;

export function grpcMetadataToCallOptions(metadata: Record<string, string | undefined>): CallOptions {
  const headers = new Headers();
  Object.entries(metadata).forEach(([k, v]) => {
    if (v !== undefined && v !== '') headers.set(k, v);
  });
  return { headers };
}

export async function grpcStreamOp<M, RESP>(
  ModelType: { fromGrpcResponse: FromGrpcResponseFnc<M, RESP> },
  service: any,
  method: string,
  metadata: Record<string, string | undefined>,
  req: any,
  fromGrpcRespParams: any,
  onData: OnMultipleDataFnc<M>,
  onError: OnErrorFnc,
  extraParams?: { waitForEnd?: boolean, maxDataCount?: number },
) {
  let dataCount = MIN_DATA_COUNT;
  if (extraParams?.maxDataCount && dataCount < extraParams.maxDataCount) dataCount = extraParams.maxDataCount;

  let resolved = false;
  let resolve: (() => void);
  let reject: ((reason?: any) => void);
  const promise = new Promise<void>((res, rej) => { resolve = res; reject = rej; });

  let data: M[] = [];

  const complete = (reason?: any) => {
    if (resolved) return;
    if (reason) reject(reason); else resolve();
    resolved = true;
  };

  const handleData = (response: RESP) => {
    try {
      data.push(ModelType.fromGrpcResponse(response, fromGrpcRespParams));
      if (data.length < dataCount) return;

      dataCount *= 2;
      if (extraParams?.maxDataCount) {
        if (dataCount > extraParams.maxDataCount) dataCount = extraParams.maxDataCount;
      } else if (dataCount > MAX_DATA_COUNT) dataCount = MAX_DATA_COUNT;
      onData(data);
    } catch (error) {
      const apiError = ApiError.fromError(error);
      onError(apiError);
      complete(apiError);
    }
    data = [];

    if (!extraParams?.waitForEnd) complete();
  };

  const handleError = (error: unknown) => {
    const apiError = ApiError.fromError(error);
    onError(apiError);
    complete(apiError);
  };

  const handleEnd = () => {
    try {
      if (data.length > 0) onData(data);
      onData([]);
    } catch (error) {
      const apiError = ApiError.fromError(error);
      onError(apiError);
      complete(apiError);
    }
    complete();
  };

  const callOpts = grpcMetadataToCallOptions(metadata);

  (async () => {
    try {
      const iterable = service[method](req, callOpts) as AsyncIterable<RESP>;
      // eslint-disable-next-line no-restricted-syntax
      for await (const response of iterable) {
        handleData(response);
      }

      handleEnd();
    } catch (error) {
      handleError(error);
    }
  })();

  return promise;
}

export async function grpcStreamOpAll<M, RESP>(
  ModelType: { fromGrpcResponse: FromGrpcResponseFnc<M, RESP> },
  service: any,
  method: string,
  metadata: Record<string, string | undefined>,
  req: any,
  fromGrpcRespParams?: any,
) {
  const allData: M[] = [];
  const onData = (d: M[]) => { allData.push(...d); };
  const onError = () => {};

  await grpcStreamOp(
    ModelType,
    service,
    method,
    metadata,
    req,
    fromGrpcRespParams,
    onData,
    onError,
    { waitForEnd: true },
  );
  return allData;
}

export async function grpcUnaryOp<M, RESP>(
  ModelType: { fromGrpcResponse: FromGrpcResponseFnc<M, RESP> },
  servis: any,
  method: string,
  metadata: Record<string, string | undefined>,
  req: any,
  fromGrpcRespParams: any,
  onData?: OnSingleDataFnc<M>,
  onError?: OnErrorFnc,
) {
  try {
    const callOpts = grpcMetadataToCallOptions(metadata);
    const resp = await servis[method](req, callOpts) as RESP;
    const e = ModelType.fromGrpcResponse(resp, fromGrpcRespParams);
    if (onData) onData(e);
    return e;
  } catch (error) {
    const apiError = ApiError.fromError(error);
    if (onError) onError(apiError);
    throw apiError;
  }
}

export async function grpcUnaryOpEx(
  transformFnc: ((resp: any, params?: any) => any) | null,
  servis: any,
  method: string,
  metadata: Record<string, string | undefined>,
  req: any,
  fncParams: any,
  onData?: OnSingleDataFnc<any>,
  onError?: OnErrorFnc,
) {
  try {
    const callOpts = grpcMetadataToCallOptions(metadata);
    const resp = await servis[method](req, callOpts);
    const e = resp && transformFnc ? transformFnc(resp, fncParams) : resp;
    if (onData) onData(e);
    return e;
  } catch (error) {
    const apiError = ApiError.fromError(error);
    if (onError) onError(apiError);
    throw apiError;
  }
}
